import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  ArrowLeft, Save, Upload, FileText, Calendar, User, Building
} from 'lucide-react';
import Select from 'react-select';
import { useAuth } from '../../contexts/AuthContext';
import { complianceService, plantService, userService } from '../../services/api';
import { extractArrayData } from '../../utils/helpers';
import LoadingSpinner from '../Common/LoadingSpinner';
import CalendarDropdown from '../Common/CalendarDropdown';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

const getValidationSchema = (isEdit) => Yup.object().shape({
  title: Yup.string()
    .min(3, 'Title must be at least 3 characters')
    .required('Title is required'),
  frequency: Yup.string()
    .required('Frequency is required'),
  AOP: Yup.string(),
  assignedTo: Yup.array()
    .min(1, 'At least one user must be assigned')
    .required('Assigned users are required'),
  license: Yup.string()
    .required('License is required'),
  remark: Yup.string(),
  dueDate: isEdit
    ? Yup.date().required('Due date is required')
    : Yup.date().min(new Date(), 'Due date must be in the future').required('Due date is required'),
  plants: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one plant is required')
    .required('Plants are required'),
  reason: Yup.string()
    .min(5, 'Reason must be at least 5 characters'),
  cloudinaryID: Yup.string(),
  status: Yup.string().oneOf(
    STATUS_OPTIONS.map((option) => option.value),
    'Invalid status selected'
  )
});


const ComplianceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch compliance for editing
  const {
    data: compl,
    isLoading: isLoadingCompliance,
    error: complianceError
  } = useQuery(
    ['compliance', id],
    () => complianceService.getComplianceById(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000
    }
  );

  const compliance = compl?.data;

  // Fetch plants for dropdown
  const { data: plantsData, isLoading: isLoadingPlants } = useQuery(
    ['plants'],
    () => plantService.getPlants(),
    {
      staleTime: 10 * 60 * 1000
    }
  );

  // Fetch users for dropdown
  const { data: usersData, isLoading: isLoadingUsers } = useQuery(
    ['users'],
    () => userService.getUsers(),
    {
      staleTime: 10 * 60 * 1000
    }
  );

  const plants = extractArrayData(plantsData);
  const users = extractArrayData(usersData);
  // Only show employees in the assignment dropdown (for both create & edit)
  const selectableUsers = (Array.isArray(users) ? users : []).filter((u) => u.role === 'employee');

  // Create/Update compliance mutation
  const mutation = useMutation(
    (data) => id ? complianceService.updateCompliance(id, data) : complianceService.createCompliance(data),
    {
      onSuccess: () => {
        toast.success(id ? 'Compliance updated successfully' : 'Compliance created successfully');
        queryClient.invalidateQueries('compliances');
        navigate('/compliances');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || `Failed to ${id ? 'update' : 'create'} compliance`);
      }
    }
  );

  // Initial values for form
  const getInitialValues = () => {
    if (compliance) {
      return {
        title: compliance.title || '',
        AOP: compliance.AOP || '',
        frequency: compliance.frequency ? compliance.frequency.toLowerCase() : '',
        dueDate: compliance.dueDate ? new Date(compliance.dueDate) : null,
        assignedTo: compliance.assignedTo?.map(user => user._id) || [],
        plants: Array.isArray(compliance.plants) && compliance.plants.length > 0
          ? compliance.plants.map(p => p._id || p)
          : (compliance.plant?._id ? [compliance.plant._id] : []),
        license: compliance.license || '',
        remark: compliance.remark || '',
        reason: compliance.reason || '',
        cloudinaryID: compliance.cloudinaryID || '',
        status: compliance.status || 'pending'
      };
    }
    return {
      title: '',
      AOP: '',
      frequency: '',
      dueDate: null,
      assignedTo: [],
      plants: [],
      license: '',
      remark: '',
      reason: '',
      cloudinaryID: '',
      status: 'pending'
    };
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    try {
      const formData = {
        ...values,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null
      };
      if (!id || user?.role !== 'admin') {
        delete formData.status;
      }
      const statusChangedByAdmin =
        id && ['admin', 'superAdmin'].includes(user?.role) && compliance?.status !== values.status;
      if (statusChangedByAdmin) {
        formData.verificationStatus = 'pending';
      }
      await mutation.mutateAsync(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  if (isLoadingCompliance) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading compliance..." />
      </div>
    );
  }

  if (complianceError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Error loading compliance</h3>
          <p className="mt-1 text-sm text-gray-500">{complianceError.message}</p>
          <button
            onClick={() => navigate('/compliances')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Compliances
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/compliances')}
            className="mr-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit Compliance' : 'Create Compliance'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {id ? 'Update compliance information' : 'Add a new compliance requirement'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Compliance Details</h3>
        </div>
        <div className="px-6 py-4">
          <Formik
            initialValues={getInitialValues()}
            validationSchema={getValidationSchema(!!id)}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Title *
                    </label>
                    <Field
                      type="text"
                      name="title"
                      id="title"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter compliance title"
                    />
                    <ErrorMessage name="title" component="p" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label htmlFor="frequency" className="block text-sm font-medium text-gray-700">
                      Frequency *
                    </label>
                    <Field
                      as="select"
                      name="frequency"
                      id="frequency"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select frequency</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="6 month">6 Month</option>
                      <option value="yearly">Yearly</option>
                      <option value="2 year">2 Year</option>
                      <option value="3 year">3 Year</option>
                      <option value="4 year">4 Year</option>
                      <option value="5 year">5 Year</option>
                    </Field>
                    <ErrorMessage name="frequency" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                <div>
                  <label htmlFor="AOP" className="block text-sm font-medium text-gray-700">
                    AOA
                  </label>
                  <Field
                    as="textarea"
                    name="AOP"
                    id="AOP"
                    rows={4}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AOA (Area of Application)"
                  />
                  <ErrorMessage name="AOP" component="p" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Assignment Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700">
                      Assigned To *
                    </label>
                    <div className="mt-1">
                      <Select
                        inputId="assignedTo"
                        isMulti
                        isClearable
                        isLoading={isLoadingUsers}
                        options={selectableUsers.map((u) => ({
                          value: u._id,
                          label: `${u.name} (${u.email})`,
                        }))}
                        value={selectableUsers
                          .filter((u) => Array.isArray(values.assignedTo) && values.assignedTo.includes(u._id))
                          .map((u) => ({ value: u._id, label: `${u.name} (${u.email})` }))}
                        onChange={(selectedOptions) =>
                          setFieldValue(
                            'assignedTo',
                            Array.isArray(selectedOptions) ? selectedOptions.map((opt) => opt.value) : []
                          )
                        }
                        placeholder={!id ? "Select employees" : "Select users"}
                        classNamePrefix="select"
                      />
                    </div>
                    <ErrorMessage name="assignedTo" component="p" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label htmlFor="plants" className="block text-sm font-medium text-gray-700">
                      Plants *
                    </label>
                    <div className="mt-1">
                      <Select
                        inputId="plants"
                        isMulti
                        isClearable
                        isLoading={isLoadingPlants}
                        options={(Array.isArray(plants) ? plants : []).map((p) => ({
                          value: p._id,
                          label: `${p.name}`,
                        }))}
                        value={(Array.isArray(plants) ? plants : [])
                          .filter((p) => Array.isArray(values.plants) && values.plants.includes(p._id))
                          .map((p) => ({ value: p._id, label: `${p.name}` }))}
                        onChange={(selectedOptions) =>
                          setFieldValue(
                            'plants',
                            Array.isArray(selectedOptions) ? selectedOptions.map((opt) => opt.value) : []
                          )
                        }
                        placeholder="Select plants"
                        classNamePrefix="select"
                      />
                    </div>
                    <ErrorMessage name="plants" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                {/* License and Remark */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="license" className="block text-sm font-medium text-gray-700">
                      License *
                    </label>
                    <Field
                      type="text"
                      name="license"
                      id="license"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter license information"
                    />
                    <ErrorMessage name="license" component="p" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label htmlFor="remark" className="block text-sm font-medium text-gray-700">
                      Remark
                    </label>
                    <Field
                      as="textarea"
                      name="remark"
                      id="remark"
                      rows={3}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter additional remarks (optional)"
                    />
                    <ErrorMessage name="remark" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date *
                  </label>
                  <div className="mt-1">
                    <CalendarDropdown
                      selectedDate={values.dueDate}
                      onDateChange={(date) => setFieldValue('dueDate', date)}
                      placeholder="Select due date"
                      minDate={id ? undefined : new Date()}
                      error={errors.dueDate && touched.dueDate}
                    />
                  </div>
                  <ErrorMessage name="dueDate" component="p" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Reason */}
                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                    Reason
                  </label>
                  <Field
                    as="textarea"
                    name="reason"
                    id="reason"
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter reason for compliance (optional)"
                  />
                  <ErrorMessage name="reason" component="p" className="text-red-500 text-sm mt-1" />
                </div>

                {id && ['admin', 'superAdmin'].includes(user?.role) && (
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Compliance Status
                    </label>
                    <Field
                      as="select"
                      name="status"
                      id="status"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="" disabled>
                        Select status
                      </option>
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="status" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                )}

                {/* Cloudinary ID */}
                {/* <div>
                  <label htmlFor="cloudinaryID" className="block text-sm font-medium text-gray-700">
                    Cloudinary ID
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <FileText className="h-4 w-4" />
                    </span>
                    <Field
                      type="text"
                      name="cloudinaryID"
                      id="cloudinaryID"
                      className="flex-1 min-w-0 block w-full px-3 py-2 border border-gray-300 rounded-none rounded-r-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter Cloudinary document ID"
                    />
                  </div>
                  <ErrorMessage name="cloudinaryID" component="p" className="text-red-500 text-sm mt-1" />
                </div> */}

                {/* Submit Button */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/compliances')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmitting || isSubmitting ? 'Saving...' : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ComplianceForm; 